import * as THREE from 'three';
import { settings } from '@/config';

/**
 * Builds the orb shader's uniform block from `settings.orb`. (Named `useOrbUniforms` to match
 * the requested architecture; in this vanilla-TS project it's a plain factory rather than a
 * React hook — call once per {@link OrbMaterial}, then mutate `.value`s live from the GUI.)
 */
export type OrbUniforms = ReturnType<typeof createOrbUniforms>;

export function createOrbUniforms() {
  const o = settings.orb;
  return {
    uTime: { value: 0 },
    uSphereCenter: { value: new THREE.Vector3() },
    uSphereRadius: { value: 1 },

    uAmberDeep: { value: new THREE.Color(o.amberDeep) },
    uAmberHot: { value: new THREE.Color(o.amberHot) },
    uAmberTint: { value: new THREE.Color(o.amberTint) },
    uDensity: { value: o.density },
    uDensityBias: { value: o.densityBias },
    uAbsorption: { value: o.absorption },
    uNoiseScale: { value: o.noiseScale },
    uFlowSpeed: { value: o.flowSpeed },
    uWarp: { value: o.warp },
    uFresnelPower: { value: o.fresnelPower },
    uFresnelStrength: { value: o.fresnelStrength },
    uIor: { value: o.ior },
    uReflectivity: { value: o.reflectivity },
    uEmissive: { value: o.emissive },
    uEdgeGlow: { value: o.edgeGlow },

    // Procedural sky fallback used when no HDR cubemap is bound (ORB_ENVMAP undefined).
    uSkyTop: { value: new THREE.Color(settings.sky.top) },
    uSkyHorizon: { value: new THREE.Color(settings.sky.horizon) },

    uEnvMap: { value: null as THREE.CubeTexture | null },
  };
}

/** Push the current `settings.orb` values back onto a live uniform block (debug GUI). */
export function syncOrbUniforms(u: OrbUniforms): void {
  const o = settings.orb;
  u.uAmberDeep.value.set(o.amberDeep);
  u.uAmberHot.value.set(o.amberHot);
  u.uAmberTint.value.set(o.amberTint);
  u.uDensity.value = o.density;
  u.uDensityBias.value = o.densityBias;
  u.uAbsorption.value = o.absorption;
  u.uNoiseScale.value = o.noiseScale;
  u.uFlowSpeed.value = o.flowSpeed;
  u.uWarp.value = o.warp;
  u.uFresnelPower.value = o.fresnelPower;
  u.uFresnelStrength.value = o.fresnelStrength;
  u.uIor.value = o.ior;
  u.uReflectivity.value = o.reflectivity;
  u.uEmissive.value = o.emissive;
  u.uEdgeGlow.value = o.edgeGlow;
}
